import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import addMinutes from 'date-fns/addMinutes';
import addMonths from 'date-fns/addMonths';
import addSeconds from 'date-fns/addSeconds';
import addWeeks from 'date-fns/addWeeks';
import differenceInMonths from 'date-fns/differenceInMonths';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import differenceInWeeks from 'date-fns/differenceInWeeks';
import differenceInYears from 'date-fns/differenceInYears';
import endOfDay from 'date-fns/endOfDay';
import _format from 'date-fns/format';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import getDaysInMonth from 'date-fns/getDaysInMonth';
import getTime from 'date-fns/getTime';
import isBefore from 'date-fns/isBefore';
import isFuture from 'date-fns/isFuture';
import isPast from 'date-fns/isPast';
import isSameDay from 'date-fns/isSameDay';
import isSameMonth from 'date-fns/isSameMonth';
import isToday from 'date-fns/isToday';
import isTomorrow from 'date-fns/isTomorrow';
import max from 'date-fns/max';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subYears from 'date-fns/subYears';

export {
  addDays,
  addHours,
  addMinutes,
  addMonths,
  addSeconds,
  addWeeks,
  differenceInMonths,
  differenceInSeconds,
  differenceInWeeks,
  differenceInYears,
  endOfDay,
  formatDistanceStrict,
  formatDistanceToNow,
  getDaysInMonth,
  getTime,
  isBefore,
  isFuture,
  isPast,
  isSameDay,
  isSameMonth,
  isToday,
  isTomorrow,
  max,
  startOfDay,
  startOfMonth,
  subYears,
};

export const format = (date: number | Date, format: string) => {
  if (typeof date === 'number') {
    return _format(new Date(date * 1000), format);
  } else {
    return _format(date, format);
  }
};

export const fromUnix = (t: number) => new Date(t * 1000);
export const toUnix = (date: number | Date) => Math.floor(getTime(date) / 1000);
export const now = () => new Date();
export const unix = () => toUnix(now());

export const serverUnix = () => {
  return unix();
};

export const add = (date: number | Date, amount: number, unit: TimeUnit) => {
  const addFn = {
    day: addDays,
    days: addDays,
    second: addSeconds,
    seconds: addSeconds,
    minute: addMinutes,
    minutes: addMinutes,
    hour: addHours,
    hours: addHours,
  }[unit];
  return addFn(date, amount);
};

export const diff = (date1: number | Date, date2: number | Date, unit: TimeUnit) => {
  const diffSeconds = differenceInSeconds(date1, date2);
  const diffHours = diffSeconds / 3600;
  const diffDays = diffHours / 24;
  const diffMinutes = diffSeconds / 60;
  return {
    second: diffSeconds,
    seconds: diffSeconds,
    minutes: diffMinutes,
    minute: diffMinutes,
    hour: diffHours,
    hours: diffHours,
    day: diffDays,
    days: diffDays,
  }[unit];
};

export const fromNow = (date: number | Date) => {
  const currentDate = now();
  const diffSeconds = differenceInSeconds(currentDate, date);
  const diffMinutes = diffSeconds / 60;
  const diffHours = diffMinutes / 60;
  const diffDays = diffHours / 24;
  const diffMonths = differenceInMonths(currentDate, date);
  if (diffSeconds < 45) {
    return 'A few moments ago';
  } else if (diffMinutes < 45) {
    const diff = Math.round(diffMinutes);
    return `${diff === 1 ? 'a' : diff} minute${diff !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 23) {
    const diff = Math.round(diffHours);
    return `${diff === 1 ? 'a' : diff} hour${diff !== 1 ? 's' : ''} ago`;
  } else if (diffDays <= 26) {
    const diff = Math.round(diffDays);
    return `${diff === 1 ? 'a' : diff} day${diff !== 1 ? 's' : ''} ago`;
  } else if (diffMonths <= 11) {
    const diff = diffMonths < 1 ? 1 : diffMonths;
    return `${diff === 1 ? 'a' : diff} month${diff !== 1 ? 's' : ''} ago`;
  } else {
    const diffYears = differenceInYears(currentDate, date);
    const diff = diffYears < 1 ? 1 : diffYears;
    return `${diff === 1 ? 'a' : diff} year${diff !== 1 ? 's' : ''} ago`;
  }
};

export function isGreaterThanBy(date1: number | Date, date2: number | Date, amount: number, unit: TimeUnit) {
  return diff(date1, date2, unit) > amount;
}

export function isInFuture(date: number | Date, amount = 0, unit: TimeUnit = 'seconds') {
  return isGreaterThanBy(date, now(), amount, unit);
}

//========================
// Time specific functions
//========================

export function parseTimeString(time: string): TimeIn24Hours {
  const [hours, minutes] = time.split(':').map((t) => parseInt(t));
  return { hours, minutes };
}

/**
 * Evaluates two time comparison expressions.
 *
 * @example
 *
 * ```ts
 * const t1: TimeIn24Hours = { hours: 10, minutes: 30 };
 * const t2: TimeIn24Hours = { hours: 13, minutes: 0};
 * console.log(compareTime(t1, '<', t2)); // true
 * console.log(compareTime(t1, '<=', t2)); // false
 * ```
 * @param t1 time in 24 hour format
 * @param t2 time in 24 hour format
 */
export function compareTime(t1: TimeIn24Hours, operator: ComparisonOperator, t2: TimeIn24Hours) {
  const t1Minutes = t1.hours * 60 + t1.minutes;
  const t2Minutes = t2.hours * 60 + t2.minutes;

  const resultMap: { [key in ComparisonOperator]: boolean } = {
    '<': t1Minutes < t2Minutes,
    '<=': t1Minutes <= t2Minutes,
    '>': t1Minutes > t2Minutes,
    '>=': t1Minutes >= t2Minutes,
    '===': t1Minutes === t2Minutes,
    '!==': t1Minutes !== t2Minutes,
  };

  return resultMap[operator];
}

export function convert24HoursTo12Hours(time: TimeIn24Hours): TimeIn12Hours {
  let { hours } = time;
  const { minutes } = time;

  if (hours === 0 || hours === 24) {
    return {
      hours: 0,
      minutes: minutes,
      meridiem: 'AM',
    };
  }

  let meridiem: TimeIn12Hours['meridiem'] = hours >= 12 ? 'PM' : 'AM';

  if (hours > 12) {
    hours %= 12;
  } else if (hours === 12) {
    meridiem = 'PM';
  }

  return {
    hours,
    minutes,
    meridiem,
  };
}

export function convert12HoursTo24Hours(time: TimeIn12Hours): TimeIn24Hours {
  let { hours } = time;
  const { minutes, meridiem } = time;

  if (meridiem === 'PM') {
    hours = hours === 12 ? hours : hours + 12;
  } else {
    hours = hours === 12 ? 0 : hours;
  }

  return { hours, minutes };
}

export function serialize24HoursTime(time: TimeIn24Hours, format: TimeFormat = 12) {
  if (format === 24) {
    /**
     * Examples:
     * 00:00 => 00:00
     * 01:00 => 01:00
     * 12:00 => 12:00
     * 13:00 => 13:00
     * 23:00 => 23:00
     */
    const { hours, minutes } = time;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * 00:00 => 12:00 AM *
   * 01:00 => 01:00 AM
   * 12:00 => 12:00 PM *
   * 13:00 => 01:00 PM
   * 23:00 => 11:00 PM
   */
  const { hours, minutes, meridiem } = convert24HoursTo12Hours(time);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${meridiem}`;
}

export function serialize12HoursTime(time: TimeIn12Hours, format: TimeFormat = 12) {
  if (format === 24) {
    /**
     * Examples:
     * 00:00 => 00:00
     * 01:00 => 01:00
     * 12:00 => 12:00
     * 13:00 => 13:00
     * 23:00 => 23:00
     */
    const { hours, minutes } = convert12HoursTo24Hours(time);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  /**
   * 00:00 => 12:00 AM *
   * 01:00 => 01:00 AM
   * 12:00 => 12:00 PM *
   * 13:00 => 01:00 PM
   * 23:00 => 11:00 PM
   */
  const { hours, minutes, meridiem } = time;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${meridiem}`;
}
